import { useState, useCallback, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Send, Upload, X, AlertCircle, CheckCircle2 } from 'lucide-react'
import { toast } from 'react-toastify'
import { cn } from '@/shared/lib/utils'
import { PrecadrageAccordion } from './precadrage-accordion'
import {
  validateFile,
  validateTotalSize,
  FILE_CONSTRAINTS,
  type PrecadrageSubmitData,
} from '../hooks'

// Format file size for display
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Existing assets options
const EXISTING_ASSETS_OPTIONS = [
  'starting_from_scratch',
  'existing_codebase',
  'existing_designs',
  'other',
] as const

type ExistingAssetsOption = (typeof EXISTING_ASSETS_OPTIONS)[number]

interface FileWithStatus {
  file: File
  valid: boolean
  error?: string
}

interface PrecadrageFormProps {
  onSubmit?: (data: PrecadrageSubmitData) => void
  isSubmitting?: boolean
  error?: string | null
}

export interface PrecadrageFormRef {
  injectField: (field: string, content: string) => void
}

interface FormFieldProps {
  label: string
  required?: boolean
  hint?: string
  error?: string
  touched?: boolean
  children: React.ReactNode
}

function FormField({ label, required, hint, error, touched, children }: FormFieldProps) {
  const showError = touched && error

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1">
        <label className="text-sm font-medium text-foreground">{label}</label>
        {required && <span className="text-sm font-medium text-accent">*</span>}
      </div>
      {children}
      {showError ? (
        <p className="flex items-center gap-1 text-xs text-destructive">
          <AlertCircle className="h-3 w-3" />
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-foreground-muted">{hint}</p>
      ) : null}
    </div>
  )
}

export const PrecadrageForm = forwardRef<PrecadrageFormRef, PrecadrageFormProps>(
  function PrecadrageForm({ onSubmit, isSubmitting = false, error }, ref) {
    const { t } = useTranslation()
    const [openAccordion, setOpenAccordion] = useState(1)
    const [formData, setFormData] = useState({
      projectName: '',
      description: '',
      objective: '',
      targetUsers: '',
      existingAssets: 'starting_from_scratch' as ExistingAssetsOption,
      constraints: '',
      inspirations: '',
    })
    const [attachments, setAttachments] = useState<FileWithStatus[]>([])

    const [touched, setTouched] = useState<Record<string, boolean>>({})
    const [dragActive, setDragActive] = useState(false)
    const [submitAttempted, setSubmitAttempted] = useState(false)

    // Expose injectField method via ref
    const injectField = useCallback(
      (field: string, content: string) => {
        // Map potential AI field names to form field names
        const fieldMapping: Record<string, string> = {
          description: 'description',
          objective: 'objective',
          targetUsers: 'targetUsers',
          targetusers: 'targetUsers',
          target_users: 'targetUsers',
          constraints: 'constraints',
          inspirations: 'inspirations',
          utilisateurs: 'targetUsers',
          cible: 'targetUsers',
          objectif: 'objective',
          contraintes: 'constraints',
        }

        const normalizedField = fieldMapping[field.toLowerCase()] || field
        const validFields = ['description', 'objective', 'targetUsers', 'constraints', 'inspirations']

        if (!validFields.includes(normalizedField)) {
          console.warn(`Invalid field for injection: ${field} (normalized: ${normalizedField})`)
          toast.error(t('precadrage.assistant.invalidField'))
          return
        }

        // Always replace the field content (don't append)
        setFormData((prev) => ({ ...prev, [normalizedField]: content }))

        // Open the appropriate accordion based on the field
        if (normalizedField === 'description') setOpenAccordion(1)
        else if (normalizedField === 'objective' || normalizedField === 'targetUsers') setOpenAccordion(2)
        else if (normalizedField === 'constraints' || normalizedField === 'inspirations') setOpenAccordion(3)

        toast.success(t('precadrage.assistant.fieldInjected'))
      },
      [t]
    )

    useImperativeHandle(ref, () => ({ injectField }), [injectField])

    const handleChange = (field: keyof typeof formData, value: string | ExistingAssetsOption) => {
      setFormData((prev) => ({ ...prev, [field]: value }))
    }

    const handleBlur = (field: string) => {
      setTouched((prev) => ({ ...prev, [field]: true }))
    }

    // Check if total size is within limits
    const validFiles = attachments.filter((a) => a.valid)
    const totalSizeValid = validateTotalSize(validFiles.map((a) => a.file))

    // Validation
    const getFieldError = (field: keyof typeof formData): string | undefined => {
      const value = formData[field]
      if (field === 'projectName' && (!value || (typeof value === 'string' && !value.trim()))) {
        return t('precadrage.form.errors.required')
      }
      if (field === 'description' && (!value || (typeof value === 'string' && !value.trim()))) {
        return t('precadrage.form.errors.required')
      }
      if (field === 'objective' && (!value || (typeof value === 'string' && !value.trim()))) {
        return t('precadrage.form.errors.required')
      }
      if (field === 'targetUsers' && (!value || (typeof value === 'string' && !value.trim()))) {
        return t('precadrage.form.errors.required')
      }
      return undefined
    }

    const isFieldTouched = (field: string) => touched[field] || submitAttempted

    const handleDrag = useCallback((e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (e.type === 'dragenter' || e.type === 'dragover') {
        setDragActive(true)
      } else if (e.type === 'dragleave') {
        setDragActive(false)
      }
    }, [])

    const addFiles = useCallback((files: File[]) => {
      const newFilesWithStatus: FileWithStatus[] = files.map((file) => {
        const validation = validateFile(file)
        return {
          file,
          valid: validation.valid,
          error: validation.error,
        }
      })
      setAttachments((prev) => [...prev, ...newFilesWithStatus])
    }, [])

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
          addFiles(Array.from(e.dataTransfer.files))
        }
      },
      [addFiles]
    )

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        addFiles(Array.from(e.target.files))
        e.target.value = ''
      }
    }

    const removeFile = (index: number) => {
      setAttachments((prev) => prev.filter((_, i) => i !== index))
    }

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault()
      setSubmitAttempted(true)

      if (!isFormValid) {
        // Open first accordion with errors
        if (getFieldError('projectName') || getFieldError('description')) {
          setOpenAccordion(1)
        } else if (getFieldError('objective') || getFieldError('targetUsers')) {
          setOpenAccordion(2)
        }
        return
      }

      onSubmit?.({
        ...formData,
        attachments: validFiles.map((a) => a.file),
      })
    }

    const isFormValid =
      formData.projectName.trim() !== '' &&
      formData.description.trim() !== '' &&
      formData.objective.trim() !== '' &&
      formData.targetUsers.trim() !== '' &&
      totalSizeValid

    const inputClasses = (field: keyof typeof formData) =>
      cn(
        'w-full rounded-lg border bg-background px-3.5 py-3 text-sm text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1',
        isFieldTouched(field) && getFieldError(field)
          ? 'border-destructive focus:border-destructive focus:ring-destructive'
          : 'border-border focus:border-accent focus:ring-accent'
      )

    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-6 p-6 lg:p-12">
        {/* Form Header */}
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-bold text-foreground lg:text-2xl">
            {t('precadrage.form.title')}
          </h1>
          <p className="text-sm text-foreground-muted">{t('precadrage.form.subtitleV2')}</p>
        </div>

        {/* Global Error */}
        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {/* Accordions */}
        <div className="flex flex-col gap-3">
          {/* Accordion 1: Project Information */}
          <PrecadrageAccordion
            number={1}
            title={t('precadrage.accordion.projectInfo')}
            isOpen={openAccordion === 1}
            onToggle={() => setOpenAccordion(openAccordion === 1 ? 0 : 1)}
          >
            <FormField
              label={t('precadrage.form.projectName')}
              required
              error={getFieldError('projectName')}
              touched={isFieldTouched('projectName')}
            >
              <input
                type="text"
                value={formData.projectName}
                onChange={(e) => handleChange('projectName', e.target.value)}
                onBlur={() => handleBlur('projectName')}
                placeholder={t('precadrage.form.projectNamePlaceholder')}
                className={inputClasses('projectName')}
              />
            </FormField>

            <FormField
              label={t('precadrage.form.description')}
              required
              hint={!getFieldError('description') ? t('precadrage.form.descriptionHint') : undefined}
              error={getFieldError('description')}
              touched={isFieldTouched('description')}
            >
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                onBlur={() => handleBlur('description')}
                placeholder={t('precadrage.form.descriptionPlaceholder')}
                rows={4}
                className={cn(inputClasses('description'), 'resize-none')}
              />
            </FormField>
          </PrecadrageAccordion>

          {/* Accordion 2: Objective & Target */}
          <PrecadrageAccordion
            number={2}
            title={t('precadrage.accordion.objectiveTarget')}
            isOpen={openAccordion === 2}
            onToggle={() => setOpenAccordion(openAccordion === 2 ? 0 : 2)}
          >
            <FormField
              label={t('precadrage.form.objective')}
              required
              error={getFieldError('objective')}
              touched={isFieldTouched('objective')}
            >
              <textarea
                value={formData.objective}
                onChange={(e) => handleChange('objective', e.target.value)}
                onBlur={() => handleBlur('objective')}
                placeholder={t('precadrage.form.objectivePlaceholder')}
                rows={3}
                className={cn(inputClasses('objective'), 'resize-none')}
              />
            </FormField>

            <FormField
              label={t('precadrage.form.targetUsers')}
              required
              error={getFieldError('targetUsers')}
              touched={isFieldTouched('targetUsers')}
            >
              <input
                type="text"
                value={formData.targetUsers}
                onChange={(e) => handleChange('targetUsers', e.target.value)}
                onBlur={() => handleBlur('targetUsers')}
                placeholder={t('precadrage.form.targetUsersPlaceholder')}
                className={inputClasses('targetUsers')}
              />
            </FormField>
          </PrecadrageAccordion>

          {/* Accordion 3: Resources & Constraints */}
          <PrecadrageAccordion
            number={3}
            title={t('precadrage.accordion.resourcesConstraints')}
            isOpen={openAccordion === 3}
            onToggle={() => setOpenAccordion(openAccordion === 3 ? 0 : 3)}
          >
            <FormField
              label={t('precadrage.form.existingAssets')}
              required
              hint={t('precadrage.form.existingAssetsHint')}
            >
              <select
                value={formData.existingAssets}
                onChange={(e) => handleChange('existingAssets', e.target.value as ExistingAssetsOption)}
                className="w-full rounded-lg border border-border bg-background px-3.5 py-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              >
                {EXISTING_ASSETS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {t(`precadrage.form.existingAssetsOptions.${option}`)}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label={t('precadrage.form.constraints')}>
              <textarea
                value={formData.constraints}
                onChange={(e) => handleChange('constraints', e.target.value)}
                placeholder={t('precadrage.form.constraintsPlaceholder')}
                rows={3}
                className="w-full resize-none rounded-lg border border-border bg-background px-3.5 py-3 text-sm text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </FormField>

            <FormField label={t('precadrage.form.inspirations')}>
              <textarea
                value={formData.inspirations}
                onChange={(e) => handleChange('inspirations', e.target.value)}
                placeholder={t('precadrage.form.inspirationsPlaceholder')}
                rows={2}
                className="w-full resize-none rounded-lg border border-border bg-background px-3.5 py-3 text-sm text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </FormField>
          </PrecadrageAccordion>

          {/* Accordion 4: Attachments */}
          <PrecadrageAccordion
            number={4}
            title={t('precadrage.accordion.attachments')}
            isOpen={openAccordion === 4}
            onToggle={() => setOpenAccordion(openAccordion === 4 ? 0 : 4)}
          >
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={cn(
                'flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-background-secondary/50 px-6 py-8 transition-colors',
                dragActive ? 'border-accent bg-accent/5' : 'border-border'
              )}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background">
                <Upload className="h-5 w-5 text-foreground-muted" />
              </div>
              <p className="text-sm text-foreground-muted">{t('precadrage.form.attachmentsDragDrop')}</p>
              <p className="text-xs text-foreground-muted">{t('precadrage.form.attachmentsFormats')}</p>
              <label className="cursor-pointer text-sm font-medium text-accent hover:underline">
                {t('precadrage.form.attachmentsBrowse')}
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,.zip"
                />
              </label>
            </div>

            {/* Total size warning */}
            {!totalSizeValid && (
              <p className="mt-2 flex items-center gap-1 text-xs text-destructive">
                <AlertCircle className="h-3 w-3" />
                {t('precadrage.form.errors.totalSizeTooLarge', {
                  max: FILE_CONSTRAINTS.maxTotalSize / (1024 * 1024),
                })}
              </p>
            )}

            {/* File list */}
            {attachments.length > 0 && (
              <div className="mt-3 flex flex-col gap-2">
                {attachments.map((item, index) => (
                  <div
                    key={index}
                    className={cn(
                      'flex items-center justify-between rounded-lg border px-3 py-2',
                      item.valid ? 'border-border bg-background' : 'border-destructive/50 bg-destructive/5'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      {item.valid ? (
                        <CheckCircle2 className="h-4 w-4 text-success" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      )}
                      <span className={cn('text-sm', item.valid ? 'text-foreground' : 'text-destructive')}>
                        {item.file.name}
                      </span>
                      <span className="text-xs text-foreground-muted">({formatFileSize(item.file.size)})</span>
                      {item.error && (
                        <span className="text-xs text-destructive">
                          -{' '}
                          {t(`precadrage.form.errors.${item.error}`, {
                            name: item.file.name,
                            max: FILE_CONSTRAINTS.maxSizePerFile / (1024 * 1024),
                          })}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="rounded p-1 text-foreground-muted hover:bg-background-secondary hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </PrecadrageAccordion>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col items-center gap-4 pt-4">
          <button
            type="submit"
            disabled={!isFormValid || isSubmitting}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent px-6 py-3.5 font-semibold text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Send className="h-[18px] w-[18px]" />
            {isSubmitting ? t('precadrage.form.submitting') : t('precadrage.form.submit')}
          </button>
          <p className="text-center text-xs text-foreground-muted">{t('precadrage.form.legalText')}</p>
        </div>
      </form>
    )
  }
)
